import { clsx } from "clsx"
import { Check, Mic, MicOff, PhoneCall } from "lucide-react"
import { Text } from "@/ui-kit"
import type { LiveKitParticipant } from "../../hooks/useLiveKit"

interface ParticipantCirclesProps {
  participants: LiveKitParticipant[]
  isMuted: boolean
  toggleMute: () => void
  lobbyMode?: {
    readyCount: number
    requiredCount: number
  }
}

export function ParticipantCircles({
  participants,
  isMuted,
  toggleMute,
  lobbyMode,
}: ParticipantCirclesProps) {
  return (
    <div className="flex items-center justify-center gap-8 py-4">
      {participants.map((p, i) => {
        const isYou = p.isLocal
        const badgeStatus = lobbyMode
          ? i < lobbyMode.readyCount ? "ready" : "waiting"
          : null

        return (
          <div key={p.identity} className="flex flex-col items-center gap-2">
            <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
              {p.isSpeaking && (
                <>
                  <div
                    className="bg-green absolute inset-0 rounded-full"
                    style={{ animation: "nectar-pulse-ring 2s ease-out infinite" }}
                  />
                  <div
                    className="bg-green absolute inset-0 rounded-full opacity-0"
                    style={{ animation: "nectar-pulse-ring 2s ease-out infinite 0.8s" }}
                  />
                </>
              )}
              {isYou ? (
                <button
                  type="button"
                  onClick={toggleMute}
                  className={clsx(
                    "relative z-10 flex size-16 items-center justify-center rounded-full transition-all duration-200",
                    p.isSpeaking
                      ? "bg-green text-white"
                      : isMuted
                        ? "bg-red text-white"
                        : "bg-gray-100 text-gray-80 hover:bg-gray-100/80",
                  )}
                >
                  {isMuted ? <MicOff className="size-7" /> : <Mic className="size-7" />}
                </button>
              ) : (
                <div
                  className={clsx(
                    "relative z-10 flex size-16 items-center justify-center rounded-full transition-all duration-200",
                    p.isSpeaking
                      ? "bg-green text-white"
                      : "bg-gray-100 text-gray-80",
                  )}
                >
                  <PhoneCall className="size-7" />
                </div>
              )}
            </div>

            <Text type="body2-regular" className={p.isSpeaking ? "text-green" : isYou ? "text-white" : "text-gray-90"}>
              {isYou ? `Participant ${p.participantIndex + 1} (You)` : `Participant ${p.participantIndex + 1}`}
            </Text>

            {badgeStatus && (
              <span
                className={clsx(
                  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
                  badgeStatus === "ready"
                    ? "bg-green/10 text-green"
                    : "border border-dashed border-gray-80 text-gray-80",
                )}
              >
                {badgeStatus === "ready" && <Check className="size-3" />}
                {badgeStatus === "ready" ? "Ready" : "Waiting..."}
              </span>
            )}

            {!lobbyMode && p.isMuted && (
              <Text type="body3-regular" className="text-gray-80">Muted</Text>
            )}
          </div>
        )
      })}
    </div>
  )
}
